import { LockStatus, Occupancy } from "@variocube/app-api";
import {
    Badge,
    classNames,
    Dialog,
    layout,
    PushButton,
    theme,
    typography,
    useBoxLockStatus,
    usePlatform,
    useStorageItemValue
} from "@variocube/cube-ui";
import { BoxItem } from "./BoxItem";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import { stylesheet } from "typestyle";
import { DateTime } from "luxon";
import { BookingOptions, getOccupancyBooking } from "./booking";
import { useLocalization } from "../../i18n";


export interface OccupancyItemProps {
    occupancy: Occupancy;
    canOpen: boolean;
    onBoxOpenChanged: (boxNumber: string, open: boolean) => any;
}

export function OccupancyItem({ occupancy, canOpen, onBoxOpenChanged }: OccupancyItemProps) {
    const [askReleaseDialog, setAskReleaseDialog] = useState(false);
    const [releaseDialog, setReleaseDialog] = useState(false);
    const [released, setReleased] = useState(occupancy.content.released)

    const platform = usePlatform();
    const { t } = useLocalization();

    const status = useBoxLockStatus(occupancy.boxNumber);
    const open = status == LockStatus.Open;

    const bookingOptions = useStorageItemValue<BookingOptions>("bookingOptions");

    useEffect(() => {
        onBoxOpenChanged(occupancy.boxNumber, open);
    }, [onBoxOpenChanged, occupancy, open]);

    const handleOpenOrRestore = useCallback(async () => {
        if (canOpen) {
            if (released) {
                await platform.updateOccupancy(occupancy.uuid, { ...occupancy.content, released: undefined });
                setReleased(false);
            }
            else if (shouldAskRelease(occupancy)) {
                setAskReleaseDialog(true);
            }
            else {
                await platform.openBoxLock(occupancy.boxNumber);
            }
        }
    }, [platform, canOpen, occupancy, released, bookingOptions]);

    const handleOpenAndKeep = useCallback(async () => {
        setAskReleaseDialog(false);
        await platform.openBoxLock(occupancy.boxNumber);
    }, [platform, occupancy, released]);

    const handleOpenAndRelease = useCallback(async () => {
        setAskReleaseDialog(false);
        setReleaseDialog(false);
        if (!released) {
            await platform.updateOccupancy(occupancy.uuid, { released: DateTime.utc().toISO() }, true);
            setReleased(true);
        }
        await platform.openBoxLock(occupancy.boxNumber);
    }, [platform, occupancy]);

    const closeAskReleaseDialog = useCallback(() => setAskReleaseDialog(false), []);
    const closeReleaseDialog = useCallback(() => setReleaseDialog(false), []);

    /**
     * Determinate if the UI should display the release locker dialog whenever
     * the customer accesses their locker
     * @param occupancy The occupancy to check
     */
    const shouldAskRelease = (occupancy: Occupancy) => {
        /**
         * Temporally use minute different because occupancy content does not have openedTimes attribute.
         * TODO: discuss how we track the opened times of occupancy
         */
        const diff = DateTime.now().diff(DateTime.fromISO(occupancy.created), 'minutes').minutes;
        return (bookingOptions && bookingOptions.enableRelease && bookingOptions.askReleaseLocker && diff > 1);
        // const occupancyOpenedTimes = occupancy.content.openedTimes;
        // return (bookingOptions && bookingOptions.enableRelease && bookingOptions.askReleaseLocker && occupancyOpenedTimes > 0)
    }

    const showReleaseButton = useMemo(() => {
        // const occupancyOpenedTimes = occupancy.content.openedTimes;
        // console.log('occupancyOpenedTimes', occupancy.content.openedTimes)
        // return !released && occupancyOpenedTimes > 0 && bookingOptions?.enableRelease === true && !bookingOptions?.askReleaseLocker;
        const diff = DateTime.now().diff(DateTime.fromISO(occupancy.created), 'minutes').minutes;
        console.log('diff', diff)
        console.log('released', released)
        console.log('bookingOptions', bookingOptions)
        console.log('openedTimes', occupancy.content.openedTimes)
        return !released && bookingOptions?.enableRelease === true && !bookingOptions?.askReleaseLocker && diff > 1;
    }, [released, occupancy, bookingOptions])

    return (
        <div className={classNames(layout.vertical, theme.lessContrast, styles.occupancyItem)}>
            <div className={styles.boxItemContainer}>
                <BoxItem number={occupancy.boxNumber} open={open} checked={open} />
                {released && (
                    <Badge error className={styles.released}>
                        {t("LockerReleased")}
                    </Badge>
                )}
            </div>
            <div className={classNames(layout.fill, layout.vertical, typography.alignCenter, layout.paddingMedium)}>
                {canOpen && (
                    <PushButton primary onClick={handleOpenOrRestore} className={styles.openButton}>
                        {released ? t("Restore") : t("Open")}
                    </PushButton>
                )}
                {showReleaseButton && (
                    <PushButton secondary onClick={() => setReleaseDialog(true)} className={styles.openButton}>
                        {t('Release')}
                    </PushButton>
                )}
            </div>
            <Dialog open={askReleaseDialog} onBackdropClick={closeAskReleaseDialog}>
                <div className={classNames(layout.vertical, typography.alignCenter)}>
                    <div className={classNames(theme.moreContrast, layout.paddingMedium)}>

                        <h1 className={typography.title}>
                            {t("Locker")} {occupancy.boxNumber}
                        </h1>

                        <p><strong>{t("ReleaseQuestion")}</strong></p>
                        <p>{t("ReleaseInfo")}</p>

                        <div className={classNames(layout.horizontal, layout.spaceBetween)}>
                            <PushButton danger onClick={handleOpenAndRelease} className={styles.openOptionButton}>
                                {t("OpenAndRelease")}
                            </PushButton>

                            <PushButton success onClick={handleOpenAndKeep} className={styles.openOptionButton}>
                                {t("OpenAndKeep")}
                            </PushButton>
                        </div>
                    </div>
                    <div className={classNames(theme.lessContrast, layout.paddingMedium)}>
                        <PushButton onClick={closeAskReleaseDialog}>
                            {t("Cancel")}
                        </PushButton>
                    </div>
                </div>
            </Dialog>
            <Dialog open={releaseDialog} onBackdropClick={closeReleaseDialog}>
                <div className={classNames(layout.vertical, typography.alignCenter)}>
                    <div className={classNames(theme.moreContrast, layout.paddingMedium)}>

                        <h1 className={typography.title}>
                            {t("Locker")} {occupancy.boxNumber}
                        </h1>

                        <p><strong>{t("ReleaseConfirmation")}</strong></p>

                        <div className={classNames(layout.horizontal, layout.center)}>
                            <PushButton danger onClick={handleOpenAndRelease} className={styles.openOptionButton}>
                                {t("OpenAndRelease")}
                            </PushButton>
                        </div>
                    </div>
                    <div className={classNames(theme.lessContrast, layout.paddingMedium)}>
                        <PushButton onClick={closeReleaseDialog}>
                            {t("Cancel")}
                        </PushButton>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

const styles = stylesheet({
    occupancyItem: {
        flex: 0,
        margin:
            "1.5em",
    }
    ,
    boxItemContainer: {
        position: "relative",
        backgroundColor:
        "var(--color-neutral)",
            padding
    :
        "1.5em 2.5em",
    }
,
    released: {
        position: "absolute",
            left: "50%",
        transform: "translateX(-50%)",
        bottom: "2rem"
    },
    openButton: {
        width: "100%",
        margin: "0.25em"
    },
    openOptionButton: {
        whiteSpace: "nowrap",
        margin: "0.25em 0.75em"
    },
})